import * as _ from 'underscore';

import { BrowserSupport } from '@tableau/browser-support';
import { SignInUrlBuilder } from '@tableau/shared-urls';
import { LoginUrlsWrapper } from '../../app/spaces/Login/LoginUrls';
import { ExternalAuthService, IExternalLoginEntryPoint } from '../../authentication/services/ExternalAuthService';
import { showMessageForUser } from '../../authentication/services/UserLockoutMessageService';
import { Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import { IError } from '../../httpClient/serverApi/types/IError';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { StartPageService } from '../../settings/services/StartPageService';
import { AuthenticationType, IPreloginCustomization } from '../../settings/types/IServerSettingsUnauthenticated';
import { FeatureFlags } from '../FeatureFlags';
import { ILocLinkifierModel, LocLinkify } from '../filters/LocLinkifierFilter';
import { CommonErrorCode, LoginErrorCodes, UserErrorCode } from '../helpers/ErrorCodes';
import { Keys } from '../helpers/Keys';
import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import { IGetSiteSamlLoginInfoResult } from '../ServerApi/types/IGetSiteSamlLoginInfoResult';
import { ErrorMessageHandler } from '../services/ErrorMessageHandler';
import { WindowPopupService } from '../../authentication/services/WindowPopupService';
import { SiteSwitchService } from '../siteManagement/services/SiteSwitchService';
import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { AuthenticationService } from '~src/authentication/services/AuthenticationService';

export interface ILoginScope extends ng.IScope {
  loginForm: ng.IFormController;
  signinQueryParams: SignInUrlBuilder.QueryParams;
  maybeLogin: () => void;
  // Nested to avoid the dot problem
  credentials: {
    username: string;
    password: string;
  };
  errorMessages: ILocLinkifierModel[];
  busy: boolean;
  autoLogin: () => ng.IPromise<void>;
  isUsernameAndPasswordEnabled: () => boolean;
  shouldDisplayWindowsCredentialsLink: () => boolean;
  windowsCredentialsDisplayName: () => string;
  autoLoginDidFail: boolean;
  initialized: boolean;
  preloginCustomization: IPreloginCustomization;
  isSiteSamlEnabled: () => boolean;
  shouldBypassSiteSaml: boolean;
  requestPasswordReset: () => string;
  passwordResetEnabled: () => boolean;
  passwordResetSuccess: boolean;
  isTouch: () => boolean;
  handleUsernameKeyEvent: (event: JQueryEventObject) => void;
  handlePasswordKeyEvent: (event: JQueryEventObject) => void;
}

export class LoginCtrl {

  public static $inject = [
    '$scope',
    '$location',
    '$cookies',
    'ErrorMessageHandler'
  ];

  constructor(
    $scope: ILoginScope,
    $location: ng.ILocationService,
    $cookies: ng.cookies.ICookiesService,
    errorMessageHandler: ErrorMessageHandler,
  ) {
    // comes from the SignInProvider - angular
    const signinQueryParams = $scope.signinQueryParams;
    const isOnlineUsernameAndPasswordOverrideByQueryString =
      signinQueryParams.auth?.toUpperCase() as AuthenticationType === AuthenticationType.External;

    // Now that we instantiate this ctrl/pug from an AngularDirective
    // it seems the angular form / custom directives (tbSignInLogo)
    // don't play nice.  They attach to the child scope instead of the scope of the controller.
    function loginForm(): ng.IFormController {
      return _.isUndefined($scope.loginForm) ? ($scope as any).$$childHead.loginForm
        : $scope.loginForm;
    }

    $scope.passwordResetSuccess = signinQueryParams.passwordResetSuccess;
    $scope.$watch('credentials.username', () => {
      $scope.shouldBypassSiteSaml = false;
    });

    $scope.requestPasswordReset = () => {
      if ($scope.preloginCustomization && $scope.preloginCustomization.resetPasswordUrl) {
        return $scope.preloginCustomization.resetPasswordUrl;
      } else {
        return LoginUrlsWrapper.getInstance().requestPasswordResetUrl();
      }
    };

    $scope.passwordResetEnabled = () => {
      return (!!$scope.preloginCustomization && !!$scope.preloginCustomization.resetPasswordUrl)
        || (ServerSettingsUnauthenticated.isPasswordResetEnabled());
    };

    $scope.isTouch = () => {
      return BrowserSupport.IsTouch();
    };

    function authenticatedRedirect() {
      // if we are a popup, window simply gets closed, no need to redirect
      if (WindowPopupService.maybeNotifyAndClose()) {
        return;
      }

      // if we're not a popup, redirect user to the appropriate place
      const redirect = signinQueryParams.redirect;
      const externalRedirect = signinQueryParams.externalRedirect;
      const isSigninToSpecificSite = _.isString(getSiteFromQueryParams());

      // If this is a multisite session and the user did not specify a url,
      // let the user choose which site to enter
      if (externalRedirect) {
        // External: does not require hash prefix
        WindowLocationService.setPathQueryFragment(externalRedirect);
      } else if (redirect) {
        Location.instance.setHash(redirect);
      } else if (getSessionManagementServiceInstance().isSessionMultiSite() && !isSigninToSpecificSite) {
        LoginUrlsWrapper.navigateToLoginSitePicker();
      } else {
        Location.instance.setHash(StartPageService.getCurrentUserStartPageForCurrentSite());
      }
    }

    function handleLoginErrorCode(errorCode: number) {
      $scope.passwordResetSuccess = false;
      $scope.errorMessages = errorMessageHandler.getSignInErrorMessages(errorCode);
      if (errorCode === LoginErrorCodes.KerberosFailed || errorCode === LoginErrorCodes.SspiFailed) {
        $scope.autoLoginDidFail = true;
      }
      if (FeatureFlags.localAuthPasswordExpirationEnabled && errorCode === UserErrorCode.UserMustResetPassword) {
        LoginUrlsWrapper.navigateToPasswordResetWithUsername($scope.credentials.username);
      }
      angular.element('input[name="username"]').focus();
    }

    $scope.maybeLogin = () => {
      $scope.errorMessages = [];
      if (AuthenticationTypeResolutionService.authenticationType() === AuthenticationType.ClientCertificate) {
        // Username and password is not valid for client certificate without fallback, so attempt to autoLogin
        $scope.autoLogin();
      } else if ($scope.isSiteSamlEnabled()) {
        $scope.busy = true;
        AuthenticationService.siteSamlLogin($scope.credentials.username)
            .then(
            (getSiteSamlLoginInfoResult: IGetSiteSamlLoginInfoResult) => {
              if (!getSiteSamlLoginInfoResult && !isSamlAuthenticationAndDesktopSamlSupported()) {
                // default Auth is local
                $scope.shouldBypassSiteSaml = true;
                $scope.busy = false;
              } else {
                // Either it is a site-saml user, or a server-wide auth and the server is configured for SAML.
                ExternalAuthService.login(signinQueryParams, getSiteSamlLoginInfoResult);
              }
            },
            (error: IError) => {
              const errorCode = error && error.code || <any>error;
              handleLoginErrorCode(errorCode);
              $scope.busy = false;
            });
      } else if (loginForm().$valid) {
        $scope.errorMessages = [];
        $scope.busy = true;
        AuthenticationService.login($scope.credentials.username, $scope.credentials.password, signinQueryParams)
            .then(
            authenticatedRedirect,
            (error: IError) => {
              if (error && error.code === CommonErrorCode.RedirectToOtherPod) {
                let redirectUrl = signinQueryParams.externalRedirect;
                if (!redirectUrl && _.isString(signinQueryParams.redirect)) {
                  redirectUrl = '/#' + signinQueryParams.redirect;
                }
                if (!redirectUrl && _.isString(getSiteFromQueryParams())) {
                  redirectUrl =  SiteSwitchService.getSiteUrlFragment(getSiteFromQueryParams());
                }
                if (!redirectUrl) {
                  redirectUrl = '/' + Location.instance.hash();
                }
                getSessionManagementServiceInstance().redirectToOtherPod(error, redirectUrl);
                return;
              }
              // Clear the password if credentials failed
              $scope.credentials.password = '';

              // This is really not how error should be handled. However, somehow in the development of the project,
              // some promise are rejected using ServerAPI.IError, and other simply with the error code. For example,
              // 401 errors returns only the code. To handle those scenarios, the error is coerced to either
              // error.code or simply error. In the future, it would be important to have a standard on how promises
              // can be rejected. A suggested approach is that promises should always be rejected with an instance of Error.
              const errorCode = error && error.code || <any>error;
              handleLoginErrorCode(errorCode);

              if (FeatureFlags.localAuthLoginRateLimitingEnabled && ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
                waitOnLoginRateLimiting(errorCode);
              } else {
                $scope.busy = false;
              }
            });
      } else if (loginForm().$error.required) {
        $scope.passwordResetSuccess = false;
        if (!$scope.credentials.username) {
          $scope.errorMessages = [LocLinkify.linkifier(Messages.enterYourUsername())];
          angular.element('input[name="username"]').focus();
        } else if (!$scope.credentials.password) {
          $scope.errorMessages = [LocLinkify.linkifier(Messages.enterYourPassword())];
          angular.element('input[name="password"]').focus();
        }
      }
    };

    $scope.shouldDisplayWindowsCredentialsLink = () => {
      return AuthenticationTypeResolutionService.isWindowsCredentialsAutoLogin() && !$scope.autoLoginDidFail;
    };

    $scope.isUsernameAndPasswordEnabled = () => {
      return isOnlineUsernameAndPasswordOverrideByQueryString ||
        (ServerSettingsUnauthenticated.isSiteSamlEnabled() && ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) ||  isDesktopSamlDisabled() ||
        (!AuthenticationTypeResolutionService.isAuthenticationExternal() && AuthenticationTypeResolutionService.authenticationType() !== AuthenticationType.ClientCertificate);
    };

    $scope.isSiteSamlEnabled = () => {
      return ServerSettingsUnauthenticated.isSiteSamlEnabled() && ServerSettingsUnauthenticated.isServerDomainFamilyLocal() && !$scope.shouldBypassSiteSaml;
    };

    $scope.autoLogin = () => {
      $scope.busy = true;
      return AuthenticationService.autoLogin(signinQueryParams)
                .then(
                  authenticatedRedirect,
                  (errorCode: number) => {
                    handleLoginErrorCode(errorCode);
                    $scope.busy = false;
                    $scope.initialized = true;
                  }
                );
    };

    $scope.windowsCredentialsDisplayName = () => getSessionManagementServiceInstance().sessionUserDisplayName();

    const licenseExpirationDateIfWithinWarningPeriod = ServerSettingsUnauthenticated.licenseExpirationDateIfWithinWarningPeriod();
    if (signinQueryParams.error) {
      // coerce to number
      const errorCode = signinQueryParams.error;
      handleLoginErrorCode(errorCode);
    } else if (licenseExpirationDateIfWithinWarningPeriod) {
      $scope.errorMessages = [
        LocLinkify.linkifier(Messages.licenseExpirationDateWarningMessage({
          LICENSE_EXPIRATION_DATE: L10n.localizedDateTime(licenseExpirationDateIfWithinWarningPeriod)
        }))
      ];
    }

    $scope.preloginCustomization = ServerSettingsUnauthenticated.preloginCustomization();

    const allowAutoSignin = !signinQueryParams.disableAutoSignin;
    if (!getSessionManagementServiceInstance().inSession() && AuthenticationTypeResolutionService.isAuthenticationAutoLogin() && allowAutoSignin) {
      // Attempt auto login unless there is a query parameter saying not to
      $scope.autoLogin();
    } else if (ServerSettingsUnauthenticated.isSiteSamlEnabled() && ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
      $scope.initialized = true;
    } else if (isOnlineUsernameAndPasswordOverrideByQueryString) {
      $scope.initialized = true;
    } else if (AuthenticationTypeResolutionService.isAuthenticationOpenId() || isSamlAuthenticationAndDesktopSamlSupported()) {
      ExternalAuthService.login(signinQueryParams);
    } else {
      $scope.initialized = true;
    }

    $scope.credentials = {
      username: '',
      password: ''
    };

    $scope.handleUsernameKeyEvent = ($event: JQueryEventObject) => {
      if($event.which === Keys.Escape) {
        $scope.credentials.username = '';
      }
    };

    $scope.handlePasswordKeyEvent = ($event: JQueryEventObject) => {
      if($event.which === Keys.Escape) {
        $scope.credentials.password = '';
      }
    };

    function isSamlAuthenticationAndDesktopSamlSupported(): boolean {
      if (AuthenticationTypeResolutionService.isAuthenticationSaml()) {
        if (signinQueryParams.client_type === 'desktop') {
          return !ServerSettingsUnauthenticated.isDesktopSamlDisabled();
        } else {
          return true;
        }
      }
      return false;
    }

    function isDesktopSamlDisabled(): boolean {
      return AuthenticationTypeResolutionService.isAuthenticationSaml() &&
        (signinQueryParams.client_type === 'desktop') && ServerSettingsUnauthenticated.isDesktopSamlDisabled();
    }

    function waitOnLoginRateLimiting(errorCode: number): void {
      if (loginForm().waitingOnRateLimiting) {
        return;
      }
      if (errorCode === UserErrorCode.LoginAttemptRejected || errorCode === LoginErrorCodes.BadCredentials) {
        loginForm().waitingOnRateLimiting = true;
        const originalMessages = $scope.errorMessages;

        showMessageForUser($scope.credentials.username, (message: string) => {
          // New message handler.
          const newMessages: ILocLinkifierModel[] = [...originalMessages];
          newMessages.push(LocLinkify.linkifier(message));
          $scope.errorMessages = newMessages;
        }, () => {
          // On complete.
          $scope.busy = false;
          loginForm().waitingOnRateLimiting = false;
          $scope.errorMessages = originalMessages;
        });
      } else {
        $scope.busy = false;
      }
    }

    function getSiteFromQueryParams(): string {
      if (_.isString(signinQueryParams.site)) {
        return signinQueryParams.site;
      } else if (_.isString(signinQueryParams.target_site)) {
        return signinQueryParams.target_site;
      }
      return null;
    }
  }
}

Module.instance().controller('LoginCtrl', LoginCtrl);
